import { ReactNode, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { SuspendedAccountScreen } from "@/components/SuspendedAccountScreen";

// Routes a suspended client may still use (sign out, read terms, contact us).
const ALLOWED_PREFIXES = ["/auth", "/terms", "/privacy", "/contact", "/reset-password"];

/**
 * Global gate for suspended CLIENT accounts. A suspended client can sign in,
 * but instead of the app they get the suspended screen on every page. Agency
 * suspension is handled separately by AgencyRoute; platform admins are never
 * gated here.
 */
export function SuspendedClientGate({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const [suspended, setSuspended] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    let active = true;
    const check = async () => {
      if (!user) {
        if (active) {
          setSuspended(false);
          setChecked(true);
        }
        return;
      }
      const { data } = await supabase
        .from("profiles")
        .select("user_type, approval_status")
        .eq("id", user.id)
        .maybeSingle();
      if (!active) return;
      setSuspended(data?.user_type === "client" && data?.approval_status === "suspended");
      setChecked(true);
    };
    if (!authLoading) check();
    return () => {
      active = false;
    };
  }, [user, authLoading]);

  const allowed = ALLOWED_PREFIXES.some((p) => location.pathname.startsWith(p));

  if (suspended && checked && !allowed) {
    return <SuspendedAccountScreen variant="client" />;
  }
  return <>{children}</>;
}

export default SuspendedClientGate;
